import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProducts } from "@/hooks/useData";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { AdminHeader, DeleteButton } from "@/components/admin/AdminUI";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/lib/site";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

const CATS = CATEGORIES.filter((c) => c !== "All");
const COLLECTIONS = ["Scandinavian Simplicity", "Modern Luxe", "Boho Chic", "Timeless Classics"];
const EMPTY = { name: "", category: "Sofas", collection: "", description: "", image_url: "", materials: "", dimensions: "", price_range: "On request", featured: false, order: 0 };

export default function ManageProducts() {
  const { data: items = [] } = useProducts();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/products/${id}`);
    qc.invalidateQueries({ queryKey: ["products"] });
    toast.success("Deleted");
  };

  return (
    <div data-testid="manage-products">
      <AdminHeader eyebrow="Store Catalogue" title="Products" onAdd={openNew} addLabel="Add Product" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-[#ECEAE4] group" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[4/3] bg-[#F6F5F2] overflow-hidden">
              <img src={imgUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <p className="label-eyebrow text-[9px] text-[#9E8773]">{p.category}</p>
              <h3 className="font-display text-lg font-medium text-[#2C2A29] mt-1 line-clamp-1">{p.name}</h3>
              <div className="flex justify-between items-center mt-2">
                {p.featured ? <span className="label-eyebrow text-[9px] text-[#9E8773]">Featured</span> : <span />}
                <div className="flex">
                  <button onClick={() => openEdit(p)} className="text-[#5E5A54] hover:text-[#2C2A29] p-2" data-testid={`edit-product-${p.id}`}><Pencil size={15} /></button>
                  <DeleteButton onConfirm={() => remove(p.id)} label="product" testid={`delete-product-${p.id}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] rounded-none border-[#ECEAE4] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Name" required value={form.name} onChange={(v) => set("name", v)} testid="p-name" />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="label-eyebrow text-[#5E5A54]">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className="rounded-none bg-white border-[#ECEAE4]" data-testid="p-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow text-[#5E5A54]">Collection</Label>
                <Select value={form.collection || "none"} onValueChange={(v) => set("collection", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-none bg-white border-[#ECEAE4]" data-testid="p-collection"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {COLLECTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} label="Product image" />
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid="p-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Materials" value={form.materials} onChange={(v) => set("materials", v)} testid="p-materials" />
              <Field label="Dimensions" value={form.dimensions} onChange={(v) => set("dimensions", v)} testid="p-dimensions" />
              <Field label="Price range" value={form.price_range} onChange={(v) => set("price_range", v)} testid="p-price" />
              <Field label="Display order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="p-order" />
            </div>
            <div className="flex items-center justify-between border border-[#ECEAE4] bg-white px-4 py-3">
              <Label className="label-eyebrow text-[#5E5A54]">Feature on homepage</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="p-featured" />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-none bg-[#2C2A29] text-[#FFFFFF] h-12 label-eyebrow text-[10px]" data-testid="p-save">
              {saving ? "Saving…" : "Save Product"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow text-[#5E5A54]">{label}</Label>
    <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid={testid} />
  </div>
);
